import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner, Modal, Tabs, Tab, Nav } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { User, MapPin, Music, Calendar, Link as LinkIcon, Award, Lock, Trash2, Plus, X } from 'react-feather';
import {getUserProfile, updateUserProfile, requestVerification, updateUserProfileSocialLinks} from '../services/api';
import { updateUserData } from '../services/auth';
import {SocialLink} from "../types/SocialLink";
import {UserProfile} from "../types/UserProfile";
import {getBasePhotoUrl} from "../services/api";

type FormControlElement = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

const ProfileSettings: React.FC = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('personal');
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const [profilePhoto, setProfilePhoto] = useState<File | null>(null);
  const [coverPhoto, setCoverPhoto] = useState<File | null>(null);

  // Personal information form
  const [personalInfo, setPersonalInfo] = useState({
    name: '',
    bio: '',
    location: '',
    genres: '',
    years_experience: '',
    contact_email: '',
    profile_photo: null,
    cover_photo: null,
  });

  // Password change form
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState<{[key: string]: string}>({});
  const [passwordSuccess, setPasswordSuccess] = useState('');

  // Social links
  const [socialLinks, setSocialLinks] = useState<SocialLink[]>([]);
  const [newSocialLink, setNewSocialLink] = useState<SocialLink>({ platform: 'facebook', url: '' });

  // Verification
  const [requestingVerification, setRequestingVerification] = useState(false);
  const [verificationSuccess, setVerificationSuccess] = useState('');

  // Delete account confirmation
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await getUserProfile();
      const userData = response.data;

      setProfile(userData);
      setPersonalInfo({
        name: userData.name || '',
        bio: userData.bio || '',
        location: userData.location || '',
        genres: userData.genres || '',
        years_experience: userData.years_experience ? String(userData.years_experience) : '',
        contact_email: userData.contact_email || '',
        cover_photo: null,
        profile_photo: null
      });

      if (userData.social_links) {
        setSocialLinks(userData.social_links);
      }

    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to load user profile');
      console.error('Error fetching user profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePersonalInfoChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setPersonalInfo(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmitPersonalInfo = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setSavingProfile(true);
      setError('');
      setSuccess('');

      const formData = new FormData();
      formData.append('name', personalInfo.name);
      formData.append('bio', personalInfo.bio);
      formData.append('location', personalInfo.location);
      formData.append('genres', personalInfo.genres);
      formData.append('years_experience', personalInfo.years_experience);
      formData.append('contact_email', personalInfo.contact_email);
      formData.append('social_links', socialLinks.toString());

      if (profilePhoto) formData.append('profile_photo', profilePhoto);
      if (coverPhoto) formData.append('cover_photo', coverPhoto);

      const response = await updateUserProfile(formData);

      setProfile(response.data.user);
      updateUserData(response.data.user);
      setSuccess(t('profileSettings.success.profileUpdated') as string);
    } catch (error: any) {
      setError(error.response?.data?.error || t('profileSettings.errors.updateFailed'));
      console.error('Error updating profile:', error);
    } finally {
      setSavingProfile(false);
      if (!error) {
        setTimeout(() => setSuccess(''), 3000);
      }
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordForm(prev => ({ ...prev, [name]: value }));

    // Clear error for this field if exists
    if (passwordErrors[name]) {
      setPasswordErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validatePasswordForm = (): boolean => {
    const errors: {[key: string]: string} = {};

    if (!passwordForm.current_password) {
      errors.current_password = t('profileSettings.errors.currentPasswordRequired');
    }

    if (!passwordForm.new_password) {
      errors.new_password = t('profileSettings.errors.newPasswordRequired');
    } else if (passwordForm.new_password.length < 8) {
      errors.new_password = t('profileSettings.errors.passwordLength');
    }

    if (!passwordForm.confirm_password) {
      errors.confirm_password = t('profileSettings.errors.confirmPasswordRequired');
    } else if (passwordForm.new_password !== passwordForm.confirm_password) {
      errors.confirm_password = t('profileSettings.errors.passwordMismatch');
    }

    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmitPasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validatePasswordForm()) {
      return;
    }

    try {
      setChangingPassword(true);
      setPasswordSuccess('');

      const formData = new FormData();
      formData.append('name', passwordForm.current_password);
      formData.append('bio', passwordForm.new_password);

      await updateUserProfile(formData);

      setPasswordSuccess(t('profileSettings.success.passwordChanged') as string);
      setPasswordForm({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });

    } catch (error: any) {
      if (error.response?.status === 403) {
        setPasswordErrors({
          current_password: t('profileSettings.errors.passwordIncorrect')
        });
      } else {
        setError(error.response?.data?.error || 'Failed to change password');
      }
      console.error('Error changing password:', error);
    } finally {
      setChangingPassword(false);
    }
  };

  const handleSocialLinkChange = (e: React.ChangeEvent<FormControlElement>) => {
    const { name, value } = e.target;
    setNewSocialLink(prev => ({ ...prev, [name]: value }));
  };

  const addSocialLink = () => {
    if (!newSocialLink.url) return;

    // Validate URL
    try {
      new URL(newSocialLink.url);
    } catch (e) {
      setError(t('profileSettings.socialLinks.invalidUrl') as string);
      return;
    }

    setSocialLinks([...socialLinks, { ...newSocialLink }]);
    setNewSocialLink({ platform: 'facebook', url: '' });
  };

  const removeSocialLink = (index: number) => {
    const updatedLinks = [...socialLinks];
    updatedLinks.splice(index, 1);
    setSocialLinks(updatedLinks);
  };

  const saveSocialLinks = async () => {
    try {
      setSavingProfile(true);
      setError('');
      setSuccess('');

      const response = await updateUserProfileSocialLinks({social_links: socialLinks});

      setProfile(response.data.user);
      setSuccess(t('profileSettings.success.profileUpdated') as string);
    } catch (error: any) {
      setError(error.response?.data?.error || t('profileSettings.errors.updateFailed'));
      console.error('Error updating social links:', error);
    } finally {
      setSavingProfile(false);

      // Auto-dismiss success message after 3 seconds
      if (!error) {
        setTimeout(() => {
          setSuccess('');
        }, 3000);
      }
    }
  };

  const handleRequestVerification = async () => {
    try {
      setRequestingVerification(true);
      setVerificationSuccess('');
      setError('');

      await requestVerification();
      setVerificationSuccess(t('profileSettings.success.verificationRequested') as string);

    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to request verification');
      console.error('Error requesting verification:', error);
    } finally {
      setRequestingVerification(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmation !== profile?.username) {
      setError(t('profileSettings.deleteAccount.confirmError') as string);
      return;
    }

    try {
      setDeleting(true);

      // In a real app, this would make an API call to delete the account
      // Since this is just a prototype, we'll simulate success

      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Redirect to logout route
      window.location.href = '/';

    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to delete account');
      console.error('Error deleting account:', error);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">{t('loading')}</p>
      </Container>
    );
  }

  // Photo handlers
  const handleProfilePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setProfilePhoto(e.target.files[0]);
    }
  };

  const handleCoverPhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setCoverPhoto(e.target.files[0]);
    }
  };

  return (
    <Container className="py-4">
      <Row className="mb-4">
        <Col>
          <h1 className="mb-1">{t('profileSettings.title')}</h1>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" onClose={() => setError('')} dismissible>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" onClose={() => setSuccess('')} dismissible>
          {success}
        </Alert>
      )}

      <Row>
        <Col lg={3} className="mb-4">
          <Card className="shadow-sm mb-4">
            <Card.Body className="text-center">
              <div
                className="profile-photo rounded-circle mx-auto mb-3"
                style={{
                  width: '120px',
                  height: '120px',
                  backgroundImage: profile?.profile_photo
                    ? `url(${getBasePhotoUrl() + profile.profile_photo})`
                    : 'linear-gradient(135deg, #6C63FF 0%, #4F46E5 100%)',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}
              />
              <h5 className="mb-1">{profile?.name}</h5>
              <p className="text-muted">@{profile?.username}</p>

              {profile?.is_verified && (
                <div className="mb-2">
                  {/*<Badge bg="primary" className="d-flex align-items-center mx-auto" style={{ width: 'fit-content' }}>*/}
                  {/*  <Award size={14} className="me-1" />*/}
                  {/*  {t('djProfile.verified')}*/}
                  {/*</Badge>*/}
                </div>
              )}

              <div className="text-center mt-3">
                <Button
                  variant="outline-primary"
                  size="sm"
                  as="a"
                  href={`/dj/${profile?.username}`}
                  target="_blank"
                >
                  {t('profileSettings.viewPublicProfile')}
                </Button>
              </div>
            </Card.Body>
          </Card>

          <Card className="shadow-sm">
            <Card.Body>
              <Nav variant="pills" className="flex-column">
                <Nav.Item>
                  <Nav.Link
                      active={activeTab === 'personal'}
                      onClick={() => setActiveTab('personal')}
                  >
                    <User size={16} className="me-2" />
                    {t('profileSettings.tabs.personal')}
                  </Nav.Link>
                </Nav.Item>

                <Nav.Item>
                  <Nav.Link
                      active={activeTab === 'social'}
                      onClick={() => setActiveTab('social')}
                  >
                    <LinkIcon size={16} className="me-2" />
                    {t('profileSettings.tabs.social')}
                  </Nav.Link>
                </Nav.Item>

                <Nav.Item>
                  <Nav.Link
                      active={activeTab === 'security'}
                      onClick={() => setActiveTab('security')}
                  >
                    <Lock size={16} className="me-2" />
                    {t('profileSettings.tabs.security')}
                  </Nav.Link>
                </Nav.Item>

                <Nav.Item>
                  <Nav.Link
                      active={activeTab === 'verification'}
                      onClick={() => setActiveTab('verification')}
                  >
                    <Award size={16} className="me-2" />
                    {t('profileSettings.tabs.verification')}
                  </Nav.Link>
                </Nav.Item>

                <Nav.Item>
                  <Nav.Link
                      active={activeTab === 'delete'}
                      onClick={() => setActiveTab('delete')}
                      className="text-danger"
                  >
                    <Trash2 size={16} className="me-2" />
                    {t('profileSettings.tabs.deleteAccount')}
                  </Nav.Link>
                </Nav.Item>
              </Nav>
            </Card.Body>
          </Card>

        </Col>

        <Col lg={9}>
          {/* Personal Information */}
          {activeTab === 'personal' && (
            <Card className="shadow-sm">
              <Card.Header className="bg-white py-3">
                <h5 className="mb-0">{t('profileSettings.personalInfo.title')}</h5>
              </Card.Header>
              <Card.Body>
                <Form onSubmit={handleSubmitPersonalInfo}>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>{t('profileSettings.personalInfo.name')}</Form.Label>
                        <Form.Control
                          type="text"
                          name="name"
                          value={personalInfo.name}
                          onChange={handlePersonalInfoChange}
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>{t('profileSettings.personalInfo.email')}</Form.Label>
                        <Form.Control
                          type="email"
                          value={profile?.email}
                          disabled
                          readOnly
                        />
                        <Form.Text className="text-muted">
                          {t('profileSettings.personalInfo.emailNote')}
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label>{t('profileSettings.personalInfo.username')}</Form.Label>
                    <Form.Control
                      type="text"
                      value={profile?.username}
                      disabled
                      readOnly
                    />
                    <Form.Text className="text-muted">
                      {t('profileSettings.personalInfo.usernameNote')}
                    </Form.Text>
                  </Form.Group>

                  {/*CONTACT EMAIL*/}
                  <Form.Group className="mb-3">
                    <Form.Label>{t('profileSettings.personalInfo.contactEmail')}</Form.Label>
                    <Form.Control
                        type="text"
                        name="contact_email"
                        value={personalInfo.contact_email}
                        onChange={handlePersonalInfoChange}
                        required
                    />
                  </Form.Group>

                  {/* PROFILE PHOTO */}
                  <Form.Group className="mb-3">
                    <Form.Label>{t('profileSettings.personalInfo.profilePhoto')}</Form.Label>
                    <Form.Control
                        type="file"
                        name="profile_photo"
                        accept="image/*"
                        onChange={handleProfilePhotoChange}
                    />
                    {personalInfo.profile_photo && (
                        <Form.Text className="text-muted">
                          Selected: {personalInfo.profile_photo}
                        </Form.Text>
                    )}
                  </Form.Group>

                  {/* COVER PHOTO */}
                  <Form.Group className="mb-3">
                    <Form.Label>{t('profileSettings.personalInfo.coverPhoto')}</Form.Label>
                    <Form.Control
                        type="file"
                        name="cover_photo"
                        accept="image/*"
                        onChange={handleCoverPhotoChange}
                    />
                    {personalInfo.cover_photo && (
                        <Form.Text className="text-muted">
                          Selected: {personalInfo.cover_photo}
                        </Form.Text>
                    )}
                  </Form.Group>


                  <Form.Group className="mb-3">
                    <Form.Label>{t('profileSettings.personalInfo.bio')}</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      name="bio"
                      value={personalInfo.bio}
                      onChange={handlePersonalInfoChange}
                      placeholder={t('profileSettings.personalInfo.bioPlaceholder') as string}
                    />
                  </Form.Group>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>{t('profileSettings.personalInfo.location')}</Form.Label>
                        <Form.Control
                          type="text"
                          name="location"
                          value={personalInfo.location}
                          onChange={handlePersonalInfoChange}
                          placeholder={t('profileSettings.personalInfo.locationPlaceholder') as string}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>{t('profileSettings.personalInfo.yearsExperience')}</Form.Label>
                        <Form.Control
                          type="number"
                          name="years_experience"
                          value={personalInfo.years_experience}
                          onChange={handlePersonalInfoChange}
                          min="0"
                          max="100"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label>{t('profileSettings.personalInfo.genres')}</Form.Label>
                    <Form.Control
                      type="text"
                      name="genres"
                      value={personalInfo.genres}
                      onChange={handlePersonalInfoChange}
                      placeholder={t('profileSettings.personalInfo.genresPlaceholder') as string}
                    />
                  </Form.Group>

                  <Button
                    variant="primary"
                    type="submit"
                    disabled={savingProfile}
                  >
                    {savingProfile ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        {t('loading')}
                      </>
                    ) : t('profileSettings.personalInfo.saveButton')}
                  </Button>
                </Form>
              </Card.Body>
            </Card>
          )}

          {/* Social Links */}
          {activeTab === 'social' && (
            <Card className="shadow-sm">
              <Card.Header className="bg-white py-3">
                <h5 className="mb-0">{t('profileSettings.socialLinks.title')}</h5>
              </Card.Header>
              <Card.Body>
                <div className="mb-4">
                  <div className="d-flex mb-3">
                    <Form.Select
                      name="platform"
                      value={newSocialLink.platform}
                      onChange={handleSocialLinkChange}
                      className="me-2"
                      style={{ maxWidth: '150px' }}
                    >
                      <option value="facebook">Facebook</option>
                      <option value="twitter">Twitter</option>
                      <option value="instagram">Instagram</option>
                      <option value="soundcloud">SoundCloud</option>
                      <option value="spotify">Spotify</option>
                      <option value="youtube">YouTube</option>
                      <option value="website">Website</option>
                    </Form.Select>
                    <Form.Control
                      type="url"
                      name="url"
                      value={newSocialLink.url}
                      onChange={handleSocialLinkChange}
                      placeholder={t('profileSettings.socialLinks.urlPlaceholder') as string}
                      className="me-2"
                    />
                    <Button
                      variant="outline-primary"
                      onClick={addSocialLink}
                    >
                      <Plus size={16} />
                    </Button>
                  </div>

                  {socialLinks.length > 0 ? (
                    <div>
                      <p className="text-muted small mb-2">{t('profileSettings.socialLinks.currentLinks')}</p>
                      {socialLinks.map((link, index) => (
                        <div key={index} className="d-flex align-items-center mb-2 p-2 border rounded">
                          <div className="flex-grow-1">
                            <strong className="text-capitalize">{link.platform}:</strong> {link.url}
                          </div>
                          <Button
                            variant="link"
                            className="text-danger"
                            onClick={() => removeSocialLink(index)}
                          >
                            <X size={16} />
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted">{t('profileSettings.socialLinks.noLinks')}</p>
                  )}
                </div>

                <Button
                  variant="primary"
                  onClick={saveSocialLinks}
                  disabled={savingProfile}
                >
                  {savingProfile ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      {t('loading')}
                    </>
                  ) : t('profileSettings.socialLinks.saveButton')}
                </Button>
              </Card.Body>
            </Card>
          )}

          {/* Security */}
          {activeTab === 'security' && (
            <Card className="shadow-sm">
              <Card.Header className="bg-white py-3">
                <h5 className="mb-0">{t('profileSettings.accountSettings.title')}</h5>
              </Card.Header>
              <Card.Body>
                {passwordSuccess && (
                  <Alert variant="success" onClose={() => setPasswordSuccess('')} dismissible>
                    {passwordSuccess}
                  </Alert>
                )}

                <Form onSubmit={handleSubmitPasswordChange}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('profileSettings.accountSettings.currentPassword')}</Form.Label>
                    <Form.Control
                      type="password"
                      name="current_password"
                      value={passwordForm.current_password}
                      onChange={handlePasswordChange}
                      isInvalid={!!passwordErrors.current_password}
                    />
                    <Form.Control.Feedback type="invalid">
                      {passwordErrors.current_password}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>{t('profileSettings.accountSettings.newPassword')}</Form.Label>
                    <Form.Control
                      type="password"
                      name="new_password"
                      value={passwordForm.new_password}
                      onChange={handlePasswordChange}
                      isInvalid={!!passwordErrors.new_password}
                    />
                    <Form.Control.Feedback type="invalid">
                      {passwordErrors.new_password}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>{t('profileSettings.accountSettings.confirmPassword')}</Form.Label>
                    <Form.Control
                      type="password"
                      name="confirm_password"
                      value={passwordForm.confirm_password}
                      onChange={handlePasswordChange}
                      isInvalid={!!passwordErrors.confirm_password}
                    />
                    <Form.Control.Feedback type="invalid">
                      {passwordErrors.confirm_password}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Button
                    variant="primary"
                    type="submit"
                    disabled={changingPassword}
                  >
                    {changingPassword ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        {t('loading')}
                      </>
                    ) : t('profileSettings.accountSettings.changePasswordButton')}
                  </Button>
                </Form>
              </Card.Body>
            </Card>
          )}

          {/* Verification */}
          {activeTab === 'verification' && (
            <Card className="shadow-sm">
              <Card.Header className="bg-white py-3">
                <h5 className="mb-0">{t('profileSettings.verificationStatus.title')}</h5>
              </Card.Header>
              <Card.Body>
                {verificationSuccess && (
                  <Alert variant="success" onClose={() => setVerificationSuccess('')} dismissible>
                    {verificationSuccess}
                  </Alert>
                )}

                <div className="d-flex align-items-center mb-4">
                  <div className={`status-indicator ${profile?.is_verified ? 'bg-success' : 'bg-secondary'} me-3`}></div>
                  <div>
                    <h5 className="mb-1">
                      {profile?.is_verified ?
                        t('profileSettings.verificationStatus.verified') :
                        t('profileSettings.verificationStatus.notVerified')
                      }
                    </h5>
                    <p className="text-muted mb-0">
                      {profile?.is_verified ?
                        t('profileSettings.verificationStatus.verifiedInfo') :
                        t('profileSettings.verificationStatus.verificationInfo')
                      }
                    </p>
                  </div>
                </div>

                {!profile?.is_verified && (
                  <>
                    <h6>{t('profileSettings.verificationStatus.requirements')}</h6>
                    <ul className="mb-4">
                      <li>{t('profileSettings.verificationStatus.requirement1')}</li>
                      <li>{t('profileSettings.verificationStatus.requirement2')}</li>
                      <li>{t('profileSettings.verificationStatus.requirement3')}</li>
                    </ul>

                    <Button
                      variant="primary"
                      onClick={handleRequestVerification}
                      disabled={requestingVerification}
                    >
                      {requestingVerification ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="me-2"
                          />
                          {t('loading')}
                        </>
                      ) : t('profileSettings.verificationStatus.requestVerification')}
                    </Button>
                  </>
                )}
              </Card.Body>
            </Card>
          )}

          {/* Delete Account */}
          {activeTab === 'delete' && (
            <Card className="shadow-sm border-danger">
              <Card.Header className="bg-danger text-white py-3">
                <h5 className="mb-0">{t('profileSettings.deleteAccount.title')}</h5>
              </Card.Header>
              <Card.Body>
                <Alert variant="danger">
                  <Alert.Heading>{t('profileSettings.deleteAccount.warning')}</Alert.Heading>
                  <p>{t('profileSettings.deleteAccount.consequences')}</p>
                </Alert>

                <p>{t('profileSettings.deleteAccount.confirmation', { username: profile?.username })}</p>

                <Form.Group className="mb-3">
                  <Form.Control
                    type="text"
                    value={deleteConfirmation}
                    onChange={(e) => setDeleteConfirmation(e.target.value)}
                    placeholder={t('profileSettings.deleteAccount.placeholderUsername') as string}
                  />
                </Form.Group>

                <Button
                  variant="danger"
                  onClick={() => setShowDeleteModal(true)}
                  disabled={deleteConfirmation !== profile?.username}
                >
                  {t('profileSettings.deleteAccount.deleteButton')}
                </Button>
              </Card.Body>
            </Card>
          )}
        </Col>
      </Row>

      {/* Delete Account Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        backdrop="static"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{t('profileSettings.deleteAccount.confirmTitle')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{t('profileSettings.deleteAccount.confirmText')}</p>
          <p className="text-danger fw-bold">{t('profileSettings.deleteAccount.irreversible')}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDeleteModal(false)}
            disabled={deleting}
          >
            {t('profileSettings.deleteAccount.cancelButton')}
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteAccount}
            disabled={deleting}
          >
            {deleting ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                {t('loading')}
              </>
            ) : t('profileSettings.deleteAccount.confirmButton')}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default ProfileSettings;
